import { useState, useMemo, useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import dayjs from "dayjs";

import { useTranslate } from "@similarweb/i18n-provider";
import { TreemapGraph } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { EmptyState, IconButton, PixelPlaceholderLoader, Tooltip } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import { ScrollContainer } from "app/components/layout/ScrollContainer";
import { useTopicsHeatmapData } from "app/pages/market-research/api-client/topicsHeatmapAPI";
import type { Topic } from "app/pages/market-research/api-client/demandSearchTrendsTopicsAPI";
import { useTopicsData } from "app/pages/market-research/api-client/demandSearchTrendsTopicsAPI";
import { parseCountryParam } from "app/pages/market-research/shared/countryResolution";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { DEMAND_ANALYSIS_HOME_STATE_NAME } from "../../shared/constants";
import { DemandAnalysisFilters } from "../demand-analysis-filters";
import { getChartConfig } from "./config";
import { TopicsHeatmapByCategory } from "./TopicsHeatmapByCategory";
import { TopicsHighlightPanel } from "./TopicsHighlightPanel";
import {
    buildTopicToCategoryMap,
    filterTopicsByCategory,
    getTopDecliningByChange,
    getTopDecliningByVolume,
    getTopGrowingByChange,
    getTopGrowingByVolume,
    navigateToTopic,
    transformToTreemapData,
} from "./utils";

const HEATMAP_HEIGHT = 850;

const formatDateRange = (trend: { currentPeriod: { date: string } }[]): string => {
    if (trend.length === 0) {
        return "";
    }
    const start = dayjs(trend[0].currentPeriod.date).format("MMM YYYY");
    const end = dayjs(trend[trend.length - 1].currentPeriod.date).format("MMM YYYY");
    return `${start} - ${end}`;
};

const TopicsHeatmapContent = ({ country }: { country: number }) => {
    const { translate } = useTranslate();
    const { data: heatmapData, isLoading: heatmapLoading, isError: heatmapError } = useTopicsHeatmapData(country);
    const { data: topicsData, isLoading: topicsLoading } = useTopicsData({ country: WORLDWIDE_COUNTRY_ID });

    const isLoading = heatmapLoading || topicsLoading;

    const categoryNames = useMemo(
        () => (topicsData?.rawData ? Object.keys(topicsData.rawData) : []),
        [topicsData?.rawData],
    );

    const [activeCategory, setActiveCategory] = useState<string>("");
    const [highlightedTopic, setHighlightedTopic] = useState<string>("");

    const effectiveCategory = activeCategory || categoryNames[0] || "";

    const topicToCategoryMap = useMemo(
        () => (topicsData?.rawData ? buildTopicToCategoryMap(topicsData.rawData) : new Map<string, string>()),
        [topicsData?.rawData],
    );

    const topics = heatmapData?.topics ?? [];

    const growingByVolume = useMemo(() => getTopGrowingByVolume(topics), [topics]);
    const growingByChange = useMemo(() => getTopGrowingByChange(topics), [topics]);
    const decliningByVolume = useMemo(() => getTopDecliningByVolume(topics), [topics]);
    const decliningByChange = useMemo(() => getTopDecliningByChange(topics), [topics]);

    const filteredTopics = useMemo(
        () =>
            effectiveCategory
                ? filterTopicsByCategory(
                      topics,
                      topicsData?.rawData?.[effectiveCategory]?.Name ?? "",
                      topicToCategoryMap,
                  )
                : topics,
        [topics, effectiveCategory, topicToCategoryMap, topicsData?.rawData],
    );

    const { nodes: treemapData, maxAbsChange } = useMemo(
        () => transformToTreemapData(filteredTopics),
        [filteredTopics],
    );

    const handleTopicClick = useCallback((topicName: string) => {
        navigateToTopic(topicName);
    }, []);

    const handleTopicSearchSelect = useCallback(
        (topic: Topic) => {
            const category = topicToCategoryMap.get(topic.Name.toLowerCase());
            if (category) {
                const categoryKey = Object.entries(topicsData?.rawData ?? {}).find(
                    ([, cat]) => cat.Name === category,
                )?.[0];
                if (categoryKey) setActiveCategory(categoryKey);
            }
            setHighlightedTopic(topic.Name);
        },
        [topicToCategoryMap, topicsData?.rawData],
    );

    const handleCategorySelect = useCallback((categoryName: string) => {
        setActiveCategory(categoryName);
        setHighlightedTopic("");
    }, []);

    const chartConfig = useMemo(
        () =>
            getChartConfig({ onTopicClick: handleTopicClick, height: HEATMAP_HEIGHT, maxAbsChange, highlightedTopic }),
        [handleTopicClick, maxAbsChange, highlightedTopic],
    );

    if (isLoading) {
        return (
            <Stack
                gap="16px"
                padding="24px"
            >
                <PixelPlaceholderLoader
                    width="100%"
                    height="200px"
                />
                <PixelPlaceholderLoader
                    width="100%"
                    height="400px"
                />
            </Stack>
        );
    }

    if (heatmapError) {
        return (
            <Stack
                gap="16px"
                padding="24px"
            >
                <Flex
                    height="400px"
                    justifyContent="center"
                    alignItems="center"
                    background={colorsPalettes.sky[100]}
                    borderRadius="6px"
                >
                    <EmptyState
                        title={translate("demand_analysis.topics_heatmap.error.title")}
                        subtitle={translate("demand_analysis.topics_heatmap.error.subtitle")}
                    />
                </Flex>
            </Stack>
        );
    }

    return (
        <Stack
            gap="20px"
            padding="24px"
        >
            <Flex
                gap="16px"
                flexWrap="wrap"
                width="100%"
            >
                <TopicsHighlightPanel
                    panelId="biggest-volume-gains"
                    title={translate("demand_analysis.topics_heatmap.highlight.biggest_volume_gains")}
                    tooltip={translate("demand_analysis.topics_heatmap.highlight.biggest_volume_gains_tooltip")}
                    topics={growingByVolume}
                />
                <TopicsHighlightPanel
                    panelId="fastest-growing"
                    title={translate("demand_analysis.topics_heatmap.highlight.fastest_growing")}
                    tooltip={translate("demand_analysis.topics_heatmap.highlight.fastest_growing_tooltip")}
                    topics={growingByChange}
                />
                <TopicsHighlightPanel
                    panelId="biggest-volume-drops"
                    title={translate("demand_analysis.topics_heatmap.highlight.biggest_volume_drops")}
                    tooltip={translate("demand_analysis.topics_heatmap.highlight.biggest_volume_drops_tooltip")}
                    topics={decliningByVolume}
                />
                <TopicsHighlightPanel
                    panelId="fastest-declining"
                    title={translate("demand_analysis.topics_heatmap.highlight.fastest_declining")}
                    tooltip={translate("demand_analysis.topics_heatmap.highlight.fastest_declining_tooltip")}
                    topics={decliningByChange}
                />
            </Flex>

            {topicsData?.rawData && (
                <TopicsHeatmapByCategory
                    dateRangeLabel={formatDateRange(topics[0]?.trend ?? [])}
                    country={country}
                    height={HEATMAP_HEIGHT}
                    data={topicsData.rawData}
                    activeCategory={effectiveCategory}
                    onCategorySelect={handleCategorySelect}
                    flattenedTopics={topicsData.flattenedTopics}
                    onTopicSearchSelect={handleTopicSearchSelect}
                >
                    {treemapData.length > 0 ? (
                        <TreemapGraph
                            graphConfig={chartConfig}
                            data={treemapData}
                            levelStyling="flat"
                        />
                    ) : (
                        <Flex
                            height="100%"
                            justifyContent="center"
                            alignItems="center"
                            background={colorsPalettes.sky[100]}
                        >
                            <EmptyState
                                title={translate("demand_analysis.topics_heatmap.empty.title")}
                                subtitle={translate("demand_analysis.topics_heatmap.empty.subtitle")}
                            />
                        </Flex>
                    )}
                </TopicsHeatmapByCategory>
            )}
        </Stack>
    );
};

export const TopicsHeatmapPage = () => {
    const { translate } = useTranslate();
    const { params } = useCurrentStateAndParams();
    const country = parseCountryParam(params.country) ?? WORLDWIDE_COUNTRY_ID;

    const handleBackClick = () => {
        void proNavigator.go(DEMAND_ANALYSIS_HOME_STATE_NAME);
    };

    return (
        <Stack height="100%">
            <Flex
                alignItems="center"
                justifyContent="space-between"
                gap="8px"
                height="60px"
                paddingX="16px"
                background={colorsPalettes.carbon[0]}
                borderBottom={`1px solid ${colorsPalettes.carbon[100]}`}
                data-automation="topics-heatmap-header"
            >
                <Flex
                    alignItems="center"
                    gap="8px"
                >
                    <Tooltip content={translate("demand_analysis.navbar.back_button.tooltip.text")}>
                        <IconButton
                            type="flat"
                            iconName="arrow-left"
                            iconSize="xs"
                            onClick={handleBackClick}
                            data-automation="topics-heatmap-back-button"
                        />
                    </Tooltip>
                    <Text
                        fontVariant="h4"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translate("demand_analysis.topics_heatmap.page.title")}
                    </Text>
                </Flex>
                <DemandAnalysisFilters />
            </Flex>
            <ScrollContainer>
                <TopicsHeatmapContent country={country} />
            </ScrollContainer>
        </Stack>
    );
};
